'use client';

import { AnimatePresence, motion } from 'motion/react';
import React, { useState } from 'react';

import RichText from '@/components/RichText';
import type { AccordionBlock as AccordionBlockProps } from '@/payload-types';
import { cn } from '@/utils/cn';
import { pushGTMEvent } from '@/utils/pushGTMEvent';

import styles from './block.module.scss';

type AccordionItemProps = {
  heading: string;
  content: Record<string, any>;
  handleToggle: () => void;
  isOpen: boolean;
};

export const AccordionItem: React.FC<AccordionItemProps> = (props) => {
  const { content, handleToggle, heading, isOpen } = props;

  return (
    <div className={styles.item}>
      <button
        type='button'
        aria-expanded={isOpen}
        aria-controls='accordion-content'
        id='accordion-heading'
        className={cn(styles.heading, { [styles.isOpen]: isOpen })}
        onClick={handleToggle}
      >
        {heading}
        <svg
          width='16'
          height='17'
          viewBox='0 0 16 17'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path d='M13.354 6.88207L8.35403 11.8821C8.30759 11.9286 8.25245 11.9654 8.19175 11.9906C8.13105 12.0158 8.06599 12.0287 8.00028 12.0287C7.93457 12.0287 7.86951 12.0158 7.80881 11.9906C7.74811 11.9654 7.69296 11.9286 7.64653 11.8821L2.64653 6.88207C2.55271 6.78825 2.5 6.66101 2.5 6.52832C2.5 6.39564 2.55271 6.26839 2.64653 6.17457C2.74035 6.08075 2.8676 6.02805 3.00028 6.02805C3.13296 6.02805 3.26021 6.08075 3.35403 6.17457L8.00028 10.8214L12.6465 6.17457C12.693 6.12812 12.7481 6.09127 12.8088 6.06613C12.8695 6.04099 12.9346 6.02805 13.0003 6.02805C13.066 6.02805 13.131 6.04099 13.1917 6.06613C13.2524 6.09127 13.3076 6.12812 13.354 6.17457C13.4005 6.22103 13.4373 6.27618 13.4625 6.33688C13.4876 6.39757 13.5006 6.46263 13.5006 6.52832C13.5006 6.59402 13.4876 6.65908 13.4625 6.71977C13.4373 6.78047 13.4005 6.83562 13.354 6.88207Z' />
        </svg>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id='accordion-content'
            aria-labelledby='accordion-heading'
            role='region'
            className={styles.content}
            initial='collapsed'
            animate='open'
            exit='collapsed'
            style={{
              willChange: 'opacity, height',
            }}
            variants={{
              open: { opacity: 1, height: 'auto' },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{ duration: 0.3, ease: [0.625, 0.05, 0, 1] }}
          >
            {content && (
              <RichText
                content={content}
                className={styles.innerContent}
                enableGutter={false}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const AccordionBlock: React.FC<AccordionBlockProps> = (props) => {
  const { items } = props;

  const [openItem, setOpenItem] = useState<number | null>(null);

  const handleToggle =
    (index: number, meta: { component_cta: string }) => () => {
      const isOpening = openItem !== index;
      if (isOpening) {
        pushGTMEvent({
          event: 'component_cta',
          component_cta: meta.component_cta,
        });
        setOpenItem(index);
      } else {
        setOpenItem(null);
      }
    };

  if (!items || !Array.isArray(items) || items.length === 0) {
    return null;
  }

  return items.map((item, index) => {
    const { content, heading } = item;

    if (!heading) {
      return null;
    }

    return (
      <AccordionItem
        key={index}
        heading={heading}
        isOpen={openItem === index}
        handleToggle={handleToggle(index, { component_cta: heading })}
        content={content as Record<string, any>}
      />
    );
  });
};
